import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Save, Trash2, Plug, ShieldCheck, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const AccountPanel = () => {
  const [me, setMe] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [env, setEnv] = useState("prod-live");
  const [savingKeys, setSavingKeys] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lev, setLev] = useState(10);
  const [margin, setMargin] = useState(1.0);
  const [auto, setAuto] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      setEnv(data.bingx_env || "prod-live");
      setLev(data.leverage ?? 10);
      setMargin(data.margin_pct ?? 1.0);
      setAuto(!!data.auto_trade_enabled);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveKeys = async () => {
    if (!apiKey.trim() || !secretKey.trim()) {
      toast.error("API Key ve Secret Key gerekli");
      return;
    }
    setSavingKeys(true);
    try {
      const { data } = await api.put("/me/bingx", { api_key: apiKey.trim(), secret_key: secretKey.trim(), env });
      setMe(data);
      setApiKey("");
      setSecretKey("");
      toast.success("BingX anahtarları kaydedildi (şifreli)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSavingKeys(false);
    }
  };

  const testKeys = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/me/bingx/test");
      toast.success(`Bağlantı başarılı · Bakiye: ${data.balance} USDT (${data.env})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bağlantı başarısız");
    } finally {
      setTesting(false);
    }
  };

  const removeKeys = async () => {
    try {
      const { data } = await api.delete("/me/bingx");
      setMe(data);
      toast.success("BingX anahtarları silindi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  const saveSettings = async (override = {}) => {
    setSavingSettings(true);
    try {
      const payload = {
        leverage: Number(lev),
        margin_pct: Number(margin),
        auto_trade_enabled: auto,
        ...override,
      };
      const { data } = await api.put("/me/settings", payload);
      setMe(data);
      setAuto(!!data.auto_trade_enabled);
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleAuto = async () => {
    const next = !auto;
    if (next && !me?.bingx_configured) {
      toast.error("Oto-işlem için önce BingX anahtarlarınızı girin");
      return;
    }
    setAuto(next);
    await saveSettings({ auto_trade_enabled: next });
  };

  return (
    <div data-testid="account-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border-subtle)" }}>
        <KeyRound className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-bold">Hesabım · BingX Bağlantısı</h3>
        {me?.bingx_configured ? (
          <span data-testid="bingx-configured-badge" className="ml-auto text-[11px] font-semibold px-2 py-1 rounded-full flex items-center gap-1" style={{ background: "rgba(16,185,129,0.15)", color: "#10B981" }}>
            <ShieldCheck className="w-3 h-3" /> Bağlı · {me?.bingx_api_key_masked}
          </span>
        ) : (
          <span className="ml-auto text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>
            Anahtar girilmedi
          </span>
        )}
      </div>

      <div className="px-5 sm:px-6 py-4 space-y-4">
        <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          Kendi BingX API ve Secret anahtarınızı girin. Anahtarlar veritabanında şifreli saklanır, tekrar gösterilmez. Oto-işlem açıkken sinyaller KENDİ hesabınızda açılır.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>BingX API Key</label>
            <input data-testid="bingx-api-key-input" type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={me?.bingx_configured ? "••• (kayıtlı, değiştirmek için yeni gir)" : "API Key"}
              className="w-full rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-blue-500"
              style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>BingX Secret Key</label>
            <input data-testid="bingx-secret-key-input" type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} placeholder={me?.bingx_configured ? "••• (kayıtlı)" : "Secret Key"}
              className="w-full rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-blue-500"
              style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Ortam</label>
            <select data-testid="bingx-env-select" value={env} onChange={(e) => setEnv(e.target.value)}
              className="rounded-lg px-3 py-2 text-sm outline-none border focus:border-blue-500"
              style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}>
              <option value="prod-live">Canlı (prod-live)</option>
              <option value="prod-vst">Demo (prod-vst)</option>
            </select>
          </div>
          <button data-testid="save-bingx-keys-button" onClick={saveKeys} disabled={savingKeys}
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-60"
            style={{ background: "#3B82F6" }}>
            {savingKeys ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Kaydet
          </button>
          <button data-testid="test-bingx-keys-button" onClick={testKeys} disabled={testing || !me?.bingx_configured}
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:brightness-110 disabled:opacity-40 border"
            style={{ borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}>
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Bağlantıyı Test Et
          </button>
          {me?.bingx_configured && (
            <button data-testid="delete-bingx-keys-button" onClick={removeKeys}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl transition-all hover:brightness-110 border"
              style={{ borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="border-t pt-4" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="text-sm font-bold flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-400" /> Oto-İşlem (kendi hesabımda)
            </div>
            <button data-testid="my-auto-trade-toggle" onClick={toggleAuto} disabled={savingSettings}
              className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-40"
              style={{ background: auto ? "#10B981" : "var(--border-subtle)" }}>
              <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: auto ? "translateX(22px)" : "translateX(4px)" }} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Kaldıraç</label>
              <input data-testid="my-leverage-input" type="number" value={lev} onChange={(e) => setLev(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-blue-500"
                style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
              <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>1-125</span>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Teminat % (giriş başına)</label>
              <input data-testid="my-margin-input" type="number" step="0.05" value={margin} onChange={(e) => setMargin(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-blue-500"
                style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
              <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>0.1-50</span>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button data-testid="save-my-settings-button" onClick={() => saveSettings()} disabled={savingSettings}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-60"
              style={{ background: "#8B5CF6" }}>
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Ayarları Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
